import { useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import {
  isLiveEnabled,
  setLiveEnabled,
  subscribe,
  getLastLiveFetchAt,
  subscribeFetched,
} from '@/lib/liveToggle';
import { relativeTime } from '@/lib/calc/marketIntel';

/**
 * LiveToggle — the in-shell "Live" pill (handoff element D). Controls whether
 * the browser fires live quote calls; OFF (default) reads only the
 * cron-populated priceCache, ON overlays live quotes. Behaviour is UNCHANGED
 * from the old floating switch — same liveToggle lib, same scoping to
 * Performance Summary; only the presentation moved into the top bar.
 *
 * Age stamps delegate to the app-wide relativeTime (lib/calc/marketIntel) so
 * the Live pill and the intel pages read the same age the same way; only the
 * pill-specific 'refreshing…'/'just now' phrasings stay local.
 */
function formatRelative(ts: number | null): string {
  if (!ts) return 'refreshing…';
  if (Date.now() - ts < 60 * 1000) return 'just now';
  return relativeTime(ts, Date.now());
}

export function LiveToggle() {
  // The Live toggle only makes sense on pages that actually consume live prices;
  // elsewhere it's noise + a footgun. Global Indexes is a subtab under
  // /performance-summary, so this single allowed route covers both.
  const location = useLocation();
  const ALLOWED = ['/performance-summary'];
  const onAllowedRoute = ALLOWED.includes(location.pathname);

  const [on, setOn] = useState<boolean>(isLiveEnabled());
  const [, setLastFetchedAt] = useState<number | null>(getLastLiveFetchAt());
  const [tick, setTick] = useState(0);

  useEffect(() => subscribe((next) => setOn(next)), []);
  useEffect(() => subscribeFetched((ts) => setLastFetchedAt(ts)), []);

  // Repaint the "refreshed Xm ago" label every 30s while ON.
  useEffect(() => {
    if (!on) return;
    const id = setInterval(() => setTick((t) => t + 1), 30 * 1000);
    return () => clearInterval(id);
  }, [on]);

  const relativeText = formatRelative(getLastLiveFetchAt());
  void tick;

  if (!onAllowedRoute) return null;

  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label="Toggle live price refresh"
      onClick={() => setLiveEnabled(!on)}
      title={on
        ? `Live prices ON — refreshed ${relativeText}`
        : 'Live prices OFF — using cached/cloud data'}
      className="inline-flex h-8 shrink-0 items-center gap-2 rounded-full border border-border py-0 pl-3 pr-1.5 text-[11.5px] font-semibold text-foreground transition-colors hover:border-primary"
    >
      <span
        className="h-[7px] w-[7px] shrink-0 rounded-full transition-colors"
        style={{ background: on ? 'var(--green)' : 'var(--text-dim)' }}
        aria-hidden="true"
      />
      <span>Live</span>
      {/* Mini switch track + knob — the handoff's on/off affordance. */}
      <span
        className="relative h-[18px] w-8 shrink-0 rounded-full transition-colors"
        style={{ background: on ? 'var(--green)' : 'var(--text-dim)' }}
        aria-hidden="true"
      >
        <span
          className="absolute top-[2px] h-[14px] w-[14px] rounded-full bg-white transition-[left,right]"
          style={{
            boxShadow: '0 1px 3px rgba(15,23,42,.35)',
            ...(on ? { right: 2 } : { left: 2 }),
          }}
        />
      </span>
    </button>
  );
}
